import { useEffect, useState } from 'react'
import { fetchRobotScheduleByLocation } from '@/api/robot-schedule.api'
import { extractScheduleIds, mapSchedulesToTimeRanges } from '../../robot-position-setup/helpers'
import type { Position } from '../../robot-position-setup/types'

interface UsePositionScheduleSyncOptions {
    robotId: string
    activeTab: string
    positionPanelTab: 'info' | 'schedule'
    selectedPositionId: string
    positions: Position[]
    setPositions: React.Dispatch<React.SetStateAction<Position[]>>
    onLoadError: (error: unknown) => void
}

export function usePositionScheduleSync({
    robotId,
    activeTab,
    positionPanelTab,
    selectedPositionId,
    positions,
    setPositions,
    onLoadError,
}: UsePositionScheduleSyncOptions) {
    const [loadingSchedulePositionId, setLoadingSchedulePositionId] =
        useState<string>('')

    const syncPositionSchedules = async (positionId: string) => {
        if (!robotId) return

        const schedules = await fetchRobotScheduleByLocation(robotId, positionId)
        setPositions((prev) =>
            prev.map((position) =>
                position.id === positionId
                    ? {
                        ...position,
                        timeRanges: mapSchedulesToTimeRanges(schedules),
                        initialScheduleIds: extractScheduleIds(schedules),
                        scheduleLoaded: true,
                    }
                    : position,
            ),
        )
    }

    useEffect(() => {
        if (activeTab !== '2' || positionPanelTab !== 'schedule') return
        if (!robotId || !selectedPositionId) return

        const selected = positions.find((position) => position.id === selectedPositionId)
        if (!selected || selected.scheduleLoaded) return

        let cancelled = false
        setLoadingSchedulePositionId(selectedPositionId)

        void fetchRobotScheduleByLocation(robotId, selectedPositionId)
            .then((schedules) => {
                if (cancelled) return

                setPositions((prev) =>
                    prev.map((position) =>
                        position.id === selectedPositionId
                            ? {
                                ...position,
                                timeRanges: mapSchedulesToTimeRanges(schedules),
                                initialScheduleIds: extractScheduleIds(schedules),
                                scheduleLoaded: true,
                            }
                            : position,
                    ),
                )
            })
            .catch((error: unknown) => {
                if (cancelled) return
                onLoadError(error)
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingSchedulePositionId('')
                }
            })

        return () => {
            cancelled = true
        }
    }, [
        activeTab,
        onLoadError,
        positionPanelTab,
        positions,
        robotId,
        selectedPositionId,
        setPositions,
    ])

    return {
        loadingSchedulePositionId,
        syncPositionSchedules,
    }
}
